import * as React from 'react';
import { Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import MultiSelectFilterList from './MultiSelectFilterList';
import { IsimpleOption } from './multiSelectFilterUtil';

interface Iprops {
    values: IsimpleOption[];
    options: IsimpleOption[];
    onApply: (selected: IsimpleOption[]) => void;
    /** Greyed prefix on the toggle, e.g. "Standard". */
    label?: string;
    /** Shown as the value when nothing is selected, e.g. "All". */
    placeholder: string;
    selectAllLabel: string;
    applyLabel: string;
    clearLabel: string;
    searchPlaceholder: string;
    emptyLabel: string;
    /** Rendered on the toggle when 2+ are selected, e.g. "{{count}} selected". */
    countLabel: (count: number) => string;
    disabled?: boolean;
    id?: string;
}

/*
 * Pill dropdown containing a searchable checkbox list with Select All and a
 * staged Apply. Selecting options edits a draft; nothing reaches the caller
 * until Apply, so closing the menu discards in-progress edits.
 *
 * Ported from the FSE app (MMG-4223) and reconciled with Web's single-select
 * FilterDropdown (components/manageInventory/FilterDropdown.tsx), which this is
 * the multi-select sibling of - same pill markup, same "Label: value" split,
 * same overflow-aware menu positioning. The shell had to be rewritten rather
 * than copied: Web is on react-bootstrap 0.32 (`open`, `noCaret`) and the FSE
 * app is on 1.6 (`show`, `as="button"`).
 */

/*
 * Sits between Dropdown.Menu and the real menu content.
 *
 * DropdownMenu clones every direct child and injects its own onSelect/onKeyDown,
 * chained *after* whatever the child already had:
 *
 *     onSelect: createChainedFunction(child.props.onSelect, onSelect,
 *                   (key, event) => this.handleClose(event, {source: 'select'}))
 *
 * React fires a synthetic `select` event when you click into a text input, so with
 * a plain <div> here the search box closed the dropdown the instant it was clicked
 * and could never be typed in. Stopping propagation does not help - handleClose is
 * chained into the same call, not reached by bubbling.
 *
 * Being a component rather than a DOM element, the injected props land here as
 * ordinary props and are deliberately not forwarded, so nothing wires them to the
 * DOM. onClick is still stopped: ticking a box must not close the menu, since the
 * point of the staged draft is to pick several and then Apply.
 */
const MenuPanel: React.FC<{
    children?: React.ReactNode;
    onSelect?: unknown;
    onKeyDown?: unknown;
}> = ({ children }) => (
    <div onClick={e => e.stopPropagation()}>{children}</div>
);

const MultiSelectFilterDropdown: React.FC<Iprops> = ({
    values,
    options,
    onApply,
    label,
    placeholder,
    selectAllLabel,
    applyLabel,
    clearLabel,
    searchPlaceholder,
    emptyLabel,
    countLabel,
    disabled = false,
    id = 'multi-select-filter-dropdown'
}) => {
    const wrapperRef = React.useRef<HTMLDivElement>(null);
    const [positionClass, setPositionClass] = React.useState('');
    const [isOpen, setIsOpen] = React.useState(false);
    const [draft, setDraft] = React.useState<IsimpleOption[]>([]);
    // Bumped on each open so the list remounts and its search term resets.
    const [openKey, setOpenKey] = React.useState(0);

    const count = values.length;
    const isDefaultValue = count === 0;

    let valuePart: string;
    if (count === 0) {
        valuePart = placeholder;
    } else if (count === 1) {
        valuePart = values[0].label;
    } else {
        valuePart = countLabel(count);
    }

    const calculatePosition = () => {
        if (!wrapperRef.current) {
            return;
        }
        const rect = wrapperRef.current.getBoundingClientRect();
        const dropdownWidth = 260;
        const wouldOverflowRight =
            rect.left + dropdownWidth > window.innerWidth - 20;
        const wouldOverflowLeft = rect.right - dropdownWidth < 20;

        if (wouldOverflowRight && wouldOverflowLeft) {
            setPositionClass('dropdown-center');
        } else if (wouldOverflowRight) {
            setPositionClass('dropdown-right');
        } else {
            setPositionClass('');
        }
    };

    // Re-seed the draft from the committed values on every open, so an abandoned
    // edit never leaks into the next one.
    const handleToggle = (nextOpen: boolean) => {
        setIsOpen(nextOpen);
        if (nextOpen) {
            setDraft(values.map(v => ({ value: v.value, label: v.label })));
            setOpenKey(prev => prev + 1);
            setTimeout(calculatePosition, 0);
        }
    };

    const handleApply = () => {
        onApply(draft);
        setIsOpen(false);
    };

    // The toggle's "✕" commits an immediate clear, matching the single-select
    // FilterDropdown's reset affordance.
    const handleClearAll = (e: React.MouseEvent) => {
        e.stopPropagation();
        onApply([]);
        setIsOpen(false);
    };

    React.useEffect(() => {
        const handleResize = () => calculatePosition();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <div
            ref={wrapperRef}
            className={`filter-dropdown-wrapper multi-select-filter${
                disabled ? ' disabled' : ''
            }`}
        >
            <Dropdown
                open={isOpen}
                onToggle={handleToggle}
                disabled={disabled}
                className="filter-dropdown"
                id={id}
            >
                <Dropdown.Toggle
                    noCaret
                    disabled={disabled}
                    className="filter-dropdown-toggle"
                >
                    <span className="filter-dropdown-text">
                        {label && (
                            <>
                                <span className="filter-label">{label}:</span>{' '}
                            </>
                        )}
                        <span className="filter-value">{valuePart}</span>
                    </span>
                    {!isDefaultValue ? (
                        <span
                            className="filter-dropdown-reset-icon-wrapper"
                            onClick={handleClearAll}
                        >
                            <FontAwesomeIcon
                                icon={faTimes}
                                className="filter-dropdown-icon filter-dropdown-reset-icon"
                            />
                        </span>
                    ) : (
                        <FontAwesomeIcon
                            icon={faChevronDown}
                            className="filter-dropdown-icon"
                        />
                    )}
                </Dropdown.Toggle>

                <Dropdown.Menu
                    className={`filter-dropdown-menu multi-select-menu ${positionClass}`}
                >
                    <MenuPanel>
                        <MultiSelectFilterList
                            key={openKey}
                            values={draft}
                            options={options}
                            onChange={setDraft}
                            selectAllLabel={selectAllLabel}
                            searchPlaceholder={searchPlaceholder}
                            emptyLabel={emptyLabel}
                        />

                        <div className="multi-select-footer">
                            <button
                                type="button"
                                className="multi-select-clear"
                                onClick={() => setDraft([])}
                            >
                                {clearLabel}
                            </button>
                            <button
                                type="button"
                                className="multi-select-apply"
                                onClick={handleApply}
                            >
                                {`${applyLabel} (${draft.length})`}
                            </button>
                        </div>
                    </MenuPanel>
                </Dropdown.Menu>
            </Dropdown>
        </div>
    );
};

export default MultiSelectFilterDropdown;
